import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ProcessTaskStatusCounts} from 'processes/dashboard/ProcessTaskStatusCounts';
import ProgressBar from 'ui-library/bs/ProgressBar';
import {GRAY_DARK} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Header2} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {RequestV2SubmissionDetails} from './RequestV2SubmissionDetailsQueryUtils';
import {REQUEST_V2_SUBMISSION_DETAILS_STYLES} from './RequestV2SubmissionDetailsStyles';

interface RequestV2SubmissionFulfillmentProgressProps {
  requestV2Submission: RequestV2SubmissionDetails;
}

const RESOLVED_STATUS_TYPES = [
  ProcessTaskStatusType.COMPLETED,
  ProcessTaskStatusType.FAILED,
  ProcessTaskStatusType.CANCELLED,
  ProcessTaskStatusType.INVALID,
];

export default function RequestV2SubmissionFulfillmentProgress(
  props: RequestV2SubmissionFulfillmentProgressProps
): JSX.Element | null {
  if (
    props.requestV2Submission.workflowTaskGroup == null ||
    isInaccessible(props.requestV2Submission.workflowTaskGroup)
  ) {
    return null;
  }

  let numTasks = 0;
  let numResolvedTasks = 0;
  for (const taskStatusCount of props.requestV2Submission.workflowTaskGroup.taskStatusCounts) {
    if (RESOLVED_STATUS_TYPES.includes(taskStatusCount.status.statusType)) {
      numResolvedTasks += taskStatusCount.count;
    }
    numTasks += taskStatusCount.count;
  }

  return (
    <div className={classNames(css(REQUEST_V2_SUBMISSION_DETAILS_STYLES.wrapper), 'u-spacing-top')}>
      <Header2 className={css(REQUEST_V2_SUBMISSION_DETAILS_STYLES.sectionHeader)}>Task progress</Header2>
      <div>
        <div className={css(styles.progressBarWrapper)}>
          <ProgressBar
            className={css(styles.progressBar)}
            value={numTasks > 0 ? (100 * numResolvedTasks) / numTasks : 0}
          />
          <div className={css(styles.progressBarText)}>
            {numResolvedTasks} / {numTasks} fulfilled&nbsp;
            <Tooltip text="The number of tasks with a Completed, Invalid, Cancelled, or Failed status">
              <span className={css(styles.progressBarIconWrapper)}>
                <BenchlingIcon icon="info" />
              </span>
            </Tooltip>
          </div>
        </div>
        <div>
          <ProcessTaskStatusCounts
            process={props.requestV2Submission.workflowTaskGroup}
            showStatusNames={true}
            wrapContents={true}
            useRows={true}
          />
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  progressBarWrapper: {
    display: 'flex',
    flexDirection: 'row',
    padding: '16px 0px',
  },
  progressBarText: {
    marginLeft: 8,
  },
  progressBar: {
    flex: 0.75,
    marginBottom: 0,
  },
  progressBarIconWrapper: {
    color: GRAY_DARK,
  },
});
